/**
 * Model Roles Panel Component
 *
 * Allows users to assign different models to purpose-based roles
 * (VLM, Small, Medium, Large). Appears inside the AI Configuration
 * panel for the default configuration.
 */

import { useState, useEffect } from 'react';
import {
  Select,
  SelectItem,
  Button,
  InlineNotification,
  Tag,
} from '@carbon/react';
import { Save, Renew } from '@carbon/icons-react';
import {
  MODEL_ROLES,
  MODEL_ROLE_ORDER,
} from '@snxt/snxt-onto-share';
import type { ModelRole, ModelRoleAssignments } from '@snxt/snxt-onto-share';
import { useUpdateAIConfig } from '../../lib/query/hooks/useAIConfigs';
import type { AIConfiguration } from '../../lib/api/ai-config.api';
import { aiConfigApi } from '../../lib/api/ai-config.api';
import './ModelRolesPanel.scss';

interface ModelRolesPanelProps {
  config: AIConfiguration;
}

export default function ModelRolesPanel({ config }: ModelRolesPanelProps) {
  const [roles, setRoles] = useState<ModelRoleAssignments>({});
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const updateMutation = useUpdateAIConfig();

  // Initialize roles from config settings
  useEffect(() => {
    const savedRoles = (config.settings?.model_roles as ModelRoleAssignments) || {};
    setRoles(savedRoles);
  }, [config.settings]);

  // Auto-fetch available models on mount using the stored config's API key
  useEffect(() => {
    let cancelled = false;
    const fetchModels = async () => {
      setIsLoadingModels(true);
      try {
        const result = await aiConfigApi.fetchModelsForConfig(config.id);
        if (!cancelled && result.models && result.models.length > 0) {
          setAvailableModels(result.models);
        } else if (!cancelled) {
          // Fallback: at least show the config's own model
          setAvailableModels(config.modelId ? [config.modelId] : []);
        }
      } catch {
        // Fallback to config's model on error
        if (!cancelled && config.modelId) {
          setAvailableModels([config.modelId]);
        }
      } finally {
        if (!cancelled) setIsLoadingModels(false);
      }
    };
    fetchModels();
    return () => { cancelled = true; };
  }, [config.id, config.modelId]);

  const handleFetchModels = async () => {
    setIsLoadingModels(true);
    try {
      const result = await aiConfigApi.fetchModelsForConfig(config.id);
      if (result.models && result.models.length > 0) {
        setAvailableModels(result.models);
      }
    } catch {
      // If fetch fails, keep whatever models we have
    } finally {
      setIsLoadingModels(false);
    }
  };

  const handleRoleChange = (role: ModelRole, modelId: string) => {
    const newRoles = { ...roles };
    if (modelId) {
      newRoles[role] = modelId;
    } else {
      delete newRoles[role];
    }
    setRoles(newRoles);
    setIsDirty(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        id: config.id,
        data: {
          settings: {
            ...config.settings,
            model_roles: roles,
          },
        },
      });
      setIsDirty(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save model roles:', error);
    }
  };

  return (
    <div className="model-roles-panel">
      <div className="model-roles-panel__header">
        <h4>Model Roles</h4>
        <div className="model-roles-panel__header-actions">
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Renew}
            iconDescription="Refresh models"
            hasIconOnly
            onClick={handleFetchModels}
            disabled={isLoadingModels}
          />
          {isDirty && (
            <Button
              kind="primary"
              size="sm"
              renderIcon={Save}
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save Roles'}
            </Button>
          )}
        </div>
      </div>

      <p className="model-roles-panel__description">
        Assign models to task-specific roles. Only <strong>Medium</strong> is required — others fall back to it if unset.
      </p>

      <div className="model-roles-panel__grid">
        {MODEL_ROLE_ORDER.map((role) => {
          const def = MODEL_ROLES[role];
          const currentValue = role === 'medium' ? (roles.medium || '') : (roles[role] || '');

          return (
            <div key={role} className="model-roles-panel__role">
              <div className="model-roles-panel__role-header">
                <span className="model-roles-panel__role-icon">{def.icon}</span>
                <span className="model-roles-panel__role-label">{def.label}</span>
                {role === 'medium' && (
                  <Tag type="blue" size="sm">Required</Tag>
                )}
                {def.fallback && !roles[role] && (
                  <Tag type="gray" size="sm">→ {MODEL_ROLES[def.fallback].label}</Tag>
                )}
              </div>
              <p className="model-roles-panel__role-desc">{def.description}</p>
              <Select
                id={`role-${role}`}
                labelText=""
                size="sm"
                value={currentValue}
                onChange={(e) => handleRoleChange(role, e.target.value)}
              >
                <SelectItem value="" text={role === 'medium' ? `${config.modelId} (default)` : 'Not set (uses fallback)'} />
                {availableModels.map((model) => (
                  <SelectItem key={model} value={model} text={model} />
                ))}
              </Select>
            </div>
          );
        })}
      </div>

      {saveSuccess && (
        <InlineNotification
          kind="success"
          title="Model roles saved"
          lowContrast
          hideCloseButton
          className="model-roles-panel__notification"
        />
      )}

      {updateMutation.isError && (
        <InlineNotification
          kind="error"
          title="Failed to save model roles"
          subtitle="Please try again"
          lowContrast
          className="model-roles-panel__notification"
        />
      )}
    </div>
  );
}

// Made with Bob
